import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MerchantProductData } from '../merchantProducts.interface';
import { OptionItem } from '../../../shared/types/api-responses.type';
import { MasterMerchantProductFormValidations } from './merchantProduct-form-validations';
import { MerchantProductsService } from '../../../services/merchantProducts/merchantProducts.service';
import { firstValueFrom } from 'rxjs';
import { showAlert } from '../../../shared/helpers/alert';
import { MerchantsService } from '../../../services/merchants/merchants.service';
import { ProvidersService } from '../../../services/providers/providers.service';
import { CategoriesService } from '../../../services/categories/categories.service';
import { ProductsService } from '../../../services/products/products.service';

@Component({
  selector: 'app-merchantProduct-form',
  templateUrl: './merchantProduct-form.component.html',
  styleUrls: ['./merchantProduct-form.component.css'],
})
export class MerchantProductFormComponent implements OnInit {
  loading: boolean = false;
  form!: FormGroup;
  readonly id: string;
  merchantProductData!: MerchantProductData;
  merchants: OptionItem[] = [];
  providers: OptionItem[] = [];
  categories: OptionItem[] = [];
  products: OptionItem[] = [];
  isChangePassword = false;
  readonly MasterMerchantProductFormValidations = MasterMerchantProductFormValidations;

  constructor(
    private readonly fb: FormBuilder,
    private readonly route: ActivatedRoute,
    private readonly merchantProductService: MerchantProductsService,
    private readonly router: Router,
    private readonly merchantService: MerchantsService,
    private readonly providerService: ProvidersService,
    private readonly categoryService: CategoriesService,
    private readonly productsService: ProductsService
  ) {
    this.id = this.route.snapshot.paramMap.get('id') ?? '';
  }

  async ngOnInit(): Promise<void> {
    this.initForm();
    await this.loadInitialData();
  }

  private initForm(): void {
    this.form = this.fb.group({
      id: [''],
      category_id: [null, Validators.required],
      merchant_code: [null, Validators.required],
      provider_id: [null, Validators.required],
      product_code: [null, Validators.required],
      fee_merchant: ['0'],
      fee_provider: ['0'],
      fee: ['0'],
      percent_price: ['0'],
    });

    this.form.valueChanges.subscribe((value) => console.log('Form Value Changed:', value));
  }

  private async loadInitialData(): Promise<void> {
    this.loading = true;
    try {
      await this.getMerchantData();
      await this.getProviderData();
      await this.getCategoryData();
      await this.getProductData();

      if (this.id) {
        await this.getMerchantProductData();
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
      this.showNotification('error', 'Error', 'Failed to load some initial data.');
    } finally {
      this.loading = false;
    }
  }

  private async getMerchantProductData(): Promise<void> {
    try {
      const { data } = await firstValueFrom(this.merchantProductService.getMerchantProductById(this.id));
      this.merchantProductData = data;
      this.patchMerchantProductData(data);
    } catch (error) {
      this.showNotification('error', 'Failed to fetch merchantProduct', JSON.stringify(error));
    }
  }

  private async getMerchantData(): Promise<void> {
    try {
      const { data } = await firstValueFrom(this.merchantService.getMerchants());
      this.merchants = data.map((merchant) => ({ label: merchant.name, value: merchant.code }));
    } catch (error) {
      this.showNotification('error', 'Failed to fetch merchants', JSON.stringify(error));
    }
  }

  private async getProviderData(): Promise<void> {
    try {
      const { data } = await firstValueFrom(this.providerService.getProviders());
      this.providers = data.map((provider) => ({ label: provider.name, value: provider.id }));
      console.log('providerOption: ', this.providers);
    } catch (error) {
      this.showNotification('error', 'Failed to fetch provider', JSON.stringify(error));
    }
  }

  private async getCategoryData(): Promise<void> {
    try {
      const { data } = await firstValueFrom(this.categoryService.getCategories());
      this.categories = data.map((category) => ({ label: category.name, value: category.id }));
      console.log('categoryOption: ', this.categories);
    } catch (error) {
      this.showNotification('error', 'Failed to fetch category', JSON.stringify(error));
    }
  }

  private async getProductData(): Promise<void> {
    try {
      const { data } = await firstValueFrom(this.productsService.getProducts());
      this.products = data.map((product) => ({ label: product.name, value: product.code }));
      console.log('productOption: ', this.products);
    } catch (error) {
      this.showNotification('error', 'Failed to fetch products', JSON.stringify(error));
    }
  }

  private patchMerchantProductData(merchantProduct: MerchantProductData): void {
    console.log('Patching MerchantProduct Data:', merchantProduct);
    console.log('Form Controls Before Patch:', this.form.controls);

    this.form.patchValue({
      id: merchantProduct.id || '',
      category_id: merchantProduct.category_id || '',
      merchant_code: merchantProduct.merchant_code || '',
      provider_id: merchantProduct.provider_id || '',
      product_code: merchantProduct.product_code || '',
      fee_merchant: merchantProduct.fee_merchant || '0',
      fee_provider: merchantProduct.fee_provider || '0',
      fee: merchantProduct.fee || '0',
      percent_price: merchantProduct.percent_price || '0',
    });

    console.log('Form Value After Patch:', this.form.value);
  }

  convertToFlatpickrDate(dateString: string): string {
    if (!dateString) return '';

    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      console.error('Invalid date format:', dateString);
      return '';
    }

    return [date.getDate().toString().padStart(2, '0'), (date.getMonth() + 1).toString().padStart(2, '0'), date.getFullYear()].join('/');
  }

  async saveData(): Promise<void> {
    console.log('Form value before saving:', this.form.value);

    if (this.id) {
      await this.updateMerchantProduct();
      return;
    }
    await this.createMerchantProduct();
  }

  async createMerchantProduct(): Promise<void> {
    this.loading = true;
    try {
      const resp = await firstValueFrom(this.merchantProductService.createMerchantProduct(this.form.value));

      if (resp.code === 0) {
        this.showNotification('success', 'Success', 'MerchantProduct created successfully');
        await this.router.navigate(['/merchant-products/list']);
      } else {
        this.showNotification('error', 'Error', 'Failed to create merchantProduct');
      }
    } catch (error) {
      this.showNotification('error', 'Error', JSON.stringify(error));
    } finally {
      this.loading = false;
    }
  }

  async updateMerchantProduct(): Promise<void> {
    this.loading = true;
    try {
      const resp = await firstValueFrom(this.merchantProductService.updateMerchantProduct(this.id, this.form.value));

      if (resp.code === 0) {
        this.showNotification('success', 'Success', 'MerchantProduct updated successfully');
        await this.router.navigate(['/merchant-products/list']);
      } else {
        this.showNotification('error', 'Error', 'Failed to update merchantProduct');
      }
    } catch (error: any) {
      console.log('Error:', error);
      this.showNotification('error', 'Error', error);
    } finally {
      this.loading = false;
    }
  }

  private showNotification(type: 'success' | 'error', title: string, text = 'Success'): void {
    showAlert({ type, title, text }).then();
  }
}
